import { useCallback, useMemo, useState } from 'react';

import { TFunction } from 'i18next';
import { CheckIcon, ChevronDownIcon, ExternalLinkIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import {
  BillingState,
  CURRENCY_OPTIONS,
  CurrencyCode,
  ModalPlanData,
  Plan,
} from '@/modules/billing/model';
import { formatPrice } from '@/modules/billing/utils';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { CONTACT_SALES_URL, PRICING_URL } from '@/util/constants';

import { PlanCard } from './PlanCard';

interface PlanPricing {
  price: number;
  seats: number;
  extraSeat?: number;
  sessions: number;
  extraSession: number;
  analyticsRetention: number;
  aiAgents: number;
}

/** Prices in smallest currency unit (cents/centavos) to match Stripe convention and reuse formatPrice. */
const PRICING: Record<CurrencyCode, { pro: PlanPricing; growth: PlanPricing }> = {
  USD: {
    pro: {
      price: 10000,
      seats: 2,
      sessions: 100,
      extraSession: 49,
      analyticsRetention: 30,
      aiAgents: 2,
    },
    growth: {
      price: 99000,
      seats: 5,
      extraSeat: 4000,
      sessions: 1000,
      extraSession: 39,
      analyticsRetention: 90,
      aiAgents: 5,
    },
  },
  BRL: {
    pro: {
      price: 50000,
      seats: 2,
      sessions: 100,
      extraSession: 250,
      analyticsRetention: 30,
      aiAgents: 2,
    },
    growth: {
      price: 500000,
      seats: 5,
      extraSeat: 20000,
      sessions: 1000,
      extraSession: 200,
      analyticsRetention: 90,
      aiAgents: 5,
    },
  },
};

const createPlanData = (plan: Plan, t: TFunction, currency: CurrencyCode): ModalPlanData | null => {
  const baseData = { title: plan.name, plan_id: plan.plan_id };
  const { pro, growth } = PRICING[currency];

  const proPrice = formatPrice(pro.price, currency);
  const proExtraSession = formatPrice(pro.extraSession, currency);
  const growthPrice = formatPrice(growth.price, currency);
  const growthExtraSeat = formatPrice(growth.extraSeat, currency);
  const growthExtraSession = formatPrice(growth.extraSession, currency);

  switch (plan.plan_id) {
    case 'enterprise':
      return null;
    case 'pro':
      return {
        ...baseData,
        subtitle: t('billing.plan_features.plan_price', { 0: proPrice }),
        body: [
          { text: t('billing.plan_features.pro.generative_ai') },
          { text: t('billing.plan_features.analytics_retention', { 0: pro.analyticsRetention }) },
          { text: t('billing.plan_features.pro.live_agent_transfer') },
          { text: t('billing.plan_features.pro.platform_ai_insights') },
          { text: t('billing.plan_features.ai_agents'), number: pro.aiAgents.toString() },
          { text: t('billing.plan_features.collaborators'), number: pro.seats.toString() },
          {
            text: t('billing.plan_features.conversations_per_month'),
            secondaryText: t('billing.plan_features.conversations_secondary', {
              0: proExtraSession,
            }),
            number: pro.sessions.toString(),
          },
        ],
      };
    case 'growth':
      return {
        ...baseData,
        subtitle: t('billing.plan_features.plan_price', { 0: growthPrice }),
        body: [
          { text: t('billing.plan_features.growth.everything_in_pro') },
          {
            text: t('billing.plan_features.analytics_retention', { 0: growth.analyticsRetention }),
          },
          { text: t('billing.plan_features.growth.advanced_branding') },
          { text: t('billing.plan_features.growth.onboarding_support') },
          { text: t('billing.plan_features.ai_agents'), number: growth.aiAgents.toString() },
          {
            text: t('billing.plan_features.collaborators'),
            number: growth.seats.toString(),
            secondaryText: t('billing.plan_features.collaborators_secondary', {
              0: growthExtraSeat,
            }),
          },
          {
            text: t('billing.plan_features.conversations_per_month'),
            secondaryText: t('billing.plan_features.conversations_secondary', {
              0: growthExtraSession,
            }),
            number:
              growth.sessions >= 1000 ? `${growth.sessions / 1000}K` : growth.sessions.toString(),
          },
        ],
      };
    default:
      return null;
  }
};

const CurrencySelector = ({
  value,
  onChange,
}: {
  value: CurrencyCode;
  onChange: (code: CurrencyCode) => void;
}) => {
  const { t } = useTranslation();
  const selected = CURRENCY_OPTIONS.find((o) => o.code === value) ?? CURRENCY_OPTIONS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="link-secondary"
          className="m-0 h-auto p-0 text-sm leading-none font-medium"
        >
          {selected.flag} {t(selected.labelKey)}
          <ChevronDownIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {CURRENCY_OPTIONS.map((opt) => (
          <DropdownMenuItem key={opt.code} onSelect={() => onChange(opt.code)}>
            {opt.flag} {t(opt.labelKey)}
            {value === opt.code ? (
              <CheckIcon className="ml-auto size-4" />
            ) : (
              <DropdownMenuShortcut className="tracking-normal">{opt.code}</DropdownMenuShortcut>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ModalUpgradePlan = ({
  onSuccess,
}: {
  onSuccess: (plan: BillingState['plan_type'], currency: CurrencyCode) => Promise<void>;
}) => {
  const { t, i18n } = useTranslation();
  const { plans, billingState, upcomingInvoice, account } = useBilling();
  const [isLoading, setIsLoading] = useState(false);

  const isStripeBilling = billingState.plan_type === 'stripe' || billingState.plan_type === 'trial';
  const canSelectCurrency = isStripeBilling && !account?.billing_provider;

  const invoiceCurrency = CURRENCY_OPTIONS.find(
    (o) => o.code === upcomingInvoice?.currency?.toUpperCase()
  )?.code;
  const defaultCurrency: CurrencyCode = i18n.language === 'pt-br' ? 'BRL' : 'USD';
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>(defaultCurrency);
  const currency = canSelectCurrency ? selectedCurrency : (invoiceCurrency ?? 'USD');

  const currencyOption = CURRENCY_OPTIONS.find((o) => o.code === currency) ?? CURRENCY_OPTIONS[0];

  const planData = useMemo(
    () => plans.map((plan) => createPlanData(plan, t, currency)).filter((plan) => plan !== null),
    [plans, t, currency]
  );

  const handlePlanSelect = useCallback(
    async (plan: BillingState['plan_type']) => {
      setIsLoading(true);
      try {
        await onSuccess(plan, currency);
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, currency]
  );

  const closeModal = useCloseModal();

  const isPlanExpired = billingState.plan_id !== 'trial' && billingState.has_expired;

  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-2xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {isPlanExpired ? t('billing.choose_plan') : t('billing.upgrade_plan')}
          </DialogTitle>
        </DialogHeader>
        <div className="relative flex flex-col items-center gap-4">
          {isStripeBilling && (
            <div className="flex w-full items-end justify-between">
              <div className="flex items-center gap-1">
                <span className="text-xs leading-4 font-medium text-muted-foreground">
                  {t('billing.prices_shown_in')}
                </span>
                {canSelectCurrency ? (
                  <CurrencySelector value={selectedCurrency} onChange={setSelectedCurrency} />
                ) : (
                  <span className="text-sm font-medium">
                    {currencyOption.flag} {t(currencyOption.labelKey)}
                  </span>
                )}
              </div>
              {canSelectCurrency && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <TriangleAlertIcon className="size-4 text-foreground" />
                  <span>{t('billing.currency_cant_change')}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-4 self-stretch">
            {planData?.map((plan) => (
              <PlanCard
                key={plan.plan_id}
                plan={plan}
                billingState={billingState}
                onPlanSelect={handlePlanSelect}
                isLoading={isLoading}
                isPromoted={plan.plan_id === 'growth'}
              />
            ))}
          </div>
          <div className="flex w-full items-center gap-4 rounded-lg border border-border p-4">
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm leading-none font-medium text-foreground">
                {t('billing.enterprise_plan')}
              </span>
              <span className="text-sm leading-5 text-muted-foreground">
                {t('billing.upgrade_plan_modal_footer')}
              </span>
            </div>
            <Button
              variant="outline-neutral"
              asChild
              onClick={() => {
                trackEvent(EventName.ContactSales);
                closeModal();
              }}
            >
              <a href={CONTACT_SALES_URL} target="_blank" rel="noreferrer">
                {t('billing.contact_sales')}
              </a>
            </Button>
          </div>
          <Button variant="link" size="sm" className="self-end hover:text-foreground" asChild>
            <a href={PRICING_URL} target="_blank" rel="noreferrer">
              <span className="px-1">{t('billing.compare_benefits')}</span>
              <ExternalLinkIcon className="size-4 text-muted-foreground" />
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalUpgradePlan;
